import { BufferGeometry, Float32BufferAttribute, Mesh, MeshBasicMaterial, Quaternion, Scene, Texture, Vector3, WebGLRenderTarget, type Material, type Object3D, type Plane, type WebGLRenderer } from 'three';
import type { FrameSpec } from '../config/frames';
import type { FrameObject } from './FrameObject';
import type { Renderer } from './Renderer';

/**
 * GPU warm-up of the try-on (A110W).
 *
 * three.js compiles a program the first time a material is drawn, and the
 * draw waits for it: MeshPhysicalMaterial with clearcoat, environment and
 * shadows, the lens material, the shadow catcher… On a phone, the first frame
 * with the glasses blocked for hundreds of milliseconds — the stutter at the
 * very moment the glasses appear. Here the programs are compiled before the
 * glasses are ever drawn (in parallel on the GPU side, polled without
 * blocking, while the camera image is already shown), and a single invisible
 * draw then does what compile() cannot reach: shadow-pass program, camera
 * background program, texture uploads and the lens composite's framebuffer copy.
 */

interface ProgramLike {
  isReady?: () => boolean;
}

/**
 * Compiles the programs of `root` for `renderer.scene` (its lights, shadow
 * state and environment) without drawing, and resolves once the GPU has
 * linked them (KHR_parallel_shader_compile). Without the extension readiness
 * cannot be observed without blocking: the driver links in the background
 * and a few frames are left to it.
 *
 * Materials must be in the state of their first real draw: three bakes
 * `transparent` into the program, so a frame first drawn while fading in is
 * compiled in its fading state (see FrameObject.setOpacity).
 *
 * Returns the number of programs prepared. `stop()` ends the wait early
 * (try-on closed, renderer disposed).
 */
export async function compilePrograms(renderer: Renderer, root: Object3D = renderer.scene, options: { timeoutMs?: number; stop?: () => boolean } = {}): Promise<number> {
  const timeoutMs = options.timeoutMs ?? 4000;
  const gl = renderer.gl;
  const programs: ProgramLike[] = [];
  const collect = (materials: Iterable<Material>) => {
    for (const material of materials) {
      const program = (gl.properties.get(material) as { currentProgram?: ProgramLike } | undefined)?.currentProgram;
      if (program && typeof program.isReady === 'function' && !programs.includes(program)) programs.push(program);
    }
  };
  // three's compile() bakes the clipping-plane count of the LAST draw into the
  // programs (it never reads material.clippingPlanes). The frame's materials
  // carry the temple cut (TempleClip, 1 plane), the rest of the scene none:
  // compile each group with the right count, or the first real draw would
  // recompile every frame program — the very stall this warm-up removes.
  const frames: Object3D[] = [];
  root.traverse(o => { if (o.name.startsWith('frame:')) frames.push(o); });
  if (!root.name.startsWith('frame:')) {
    const detached = frames.map(f => ({ f, parent: f.parent }));
    for (const { f, parent } of detached) parent?.remove(f);
    try {
      primeClipping(renderer, []);
      collect(gl.compile(root, renderer.camera, renderer.scene));
    } finally {
      for (const { f, parent } of detached) parent?.add(f);
    }
  }
  for (const f of frames) {
    primeClipping(renderer, framePlanes(f));
    collect(gl.compile(f, renderer.camera, renderer.scene));
  }
  const parallel = gl.extensions.has('KHR_parallel_shader_compile');
  const start = performance.now();
  await new Promise<void>((resolve) => {
    const check = () => {
      const elapsed = performance.now() - start;
      if (options.stop?.() || elapsed >= timeoutMs) { resolve(); return; }
      const ready = parallel ? programs.every((p) => p.isReady!()) : elapsed >= 48;
      if (ready) resolve();
      else setTimeout(check, 16);
    };
    setTimeout(check, 0);
  });
  return programs.length;
}

/** Clipping planes carried by a frame's materials (the temple cut), [] if none. */
function framePlanes(frame: Object3D): Plane[] {
  let planes: Plane[] = [];
  frame.traverse(o => {
    if (planes.length || !(o instanceof Mesh)) return;
    const m = (Array.isArray(o.material) ? o.material[0] : o.material) as Material | undefined;
    if (m?.clippingPlanes?.length) planes = m.clippingPlanes;
  });
  return planes;
}

interface Primer { scene: Scene; geometry: BufferGeometry; material: MeshBasicMaterial; target: WebGLRenderTarget }
/**
 * A110Y: one primer per WebGL renderer, disposed with it (`disposeWarmup`). The primer used to be
 * a module singleton: three adds a `dispose` listener to every material and geometry it draws,
 * and that listener is a closure over the renderer's whole internal state (canvas, properties,
 * programs). A never-disposed singleton therefore kept the GL state of every closed session
 * alive — a heap snapshot after a close showed the canvas and the renderer internals retained
 * through `primer.material._listeners.dispose`.
 */
const primers = new WeakMap<WebGLRenderer, Primer>();

/** Releases the warm-up objects created for this renderer (called by Renderer.dispose). */
export function disposeWarmup(gl: WebGLRenderer): void {
  const primer = primers.get(gl);
  if (!primer) return;
  primers.delete(gl);
  primer.material.dispose();
  primer.geometry.dispose();
  primer.target.dispose();
}

/**
 * Sets three's current clipping state to `planes.length` planes without
 * drawing anything visible: one degenerate, colourless triangle drawn with a
 * material carrying those planes, into a 1×1 offscreen target so the canvas
 * (camera image) is never touched (compile() then reuses that state).
 */
function primeClipping(renderer: Renderer, planes: Plane[]): void {
  let primer = primers.get(renderer.gl);
  if (!primer) {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0, 0, 0, 0, 0, 0, 0], 3));
    const material = new MeshBasicMaterial({ colorWrite: false, depthWrite: false, depthTest: false });
    const mesh = new Mesh(geometry, material);
    mesh.frustumCulled = false;
    const scene = new Scene();
    scene.add(mesh);
    primer = { scene, geometry, material, target: new WebGLRenderTarget(1, 1) };
    primers.set(renderer.gl, primer);
  }
  primer.material.clippingPlanes = planes;
  const gl = renderer.gl;
  const autoClear = gl.autoClear;
  const autoUpdate = gl.shadowMap.autoUpdate;
  const target = gl.getRenderTarget();
  gl.autoClear = false;
  gl.shadowMap.autoUpdate = false;
  gl.setRenderTarget(primer.target);
  try {
    gl.render(primer.scene, renderer.camera);
  } finally {
    gl.setRenderTarget(target);
    gl.autoClear = autoClear;
    gl.shadowMap.autoUpdate = autoUpdate;
  }
}

/** Loaded textures of `root` (photos, maps) not yet on the GPU — to upload ahead of the first draw, one at a time if needed. */
export function pendingTextures(root: Object3D): Texture[] {
  const found: Texture[] = [];
  root.traverse((o) => {
    if (!(o instanceof Mesh)) return;
    for (const m of (Array.isArray(o.material) ? o.material : [o.material]) as Material[]) {
      for (const value of Object.values(m)) {
        if (!(value instanceof Texture) || found.includes(value)) continue;
        const image = value.image as { complete?: boolean; width?: number } | undefined;
        if (image && image.complete !== false && (image.width ?? 1) > 0) found.push(value);
      }
    }
  });
  return found;
}

/** Uploads the textures of `root` now rather than on its first draw. */
export function uploadTextures(renderer: Renderer, root: Object3D): void {
  for (const texture of pendingTextures(root)) renderer.gl.initTexture(texture);
}

const WARM_POSITION = new Vector3(0, 0, -450);
const WARM_QUATERNION = new Quaternion();

/**
 * One full draw with the frame posed in front of the camera (shadow pass,
 * background, lens composite, texture uploads), then a transparent canvas
 * again. Call in a task that draws the real frame afterwards (or while the
 * canvas is covered): nothing of this draw is ever presented.
 */
export function warmDraw(renderer: Renderer, spec: FrameSpec, background: HTMLVideoElement | HTMLCanvasElement | null, fadeOpacity = 0.5): void {
  const savedBackground = renderer.scene.background;
  if (background) renderer.setCameraFrame(background, true);
  renderer.render(WARM_POSITION, WARM_QUATERNION, spec, fadeOpacity, 0, null);
  renderer.getFrame()?.setOpacity(0);
  renderer.scene.background = savedBackground;
  renderer.gl.clear();
}

/**
 * Frame switch: the new model's programs and textures before it replaces the
 * current one (the old one keeps being drawn meanwhile). `opacity` is the
 * opacity it will first be drawn at.
 */
export async function prepareFrame(renderer: Renderer, frame: FrameObject, _opacity: number, stop?: () => boolean): Promise<void> {
  // A110Y: always compiled in the fading state. A solid material compiled at opacity 1 takes
  // `#define OPAQUE` (alpha forced to 1) and three does not recompile it when `transparent`
  // flips later: it would never fade. Compiled transparent, it renders the same at opacity 1.
  frame.invalidateOpacity();
  frame.setOpacity(0.5);
  // The temple cut first: its plane count is part of every frame program.
  renderer.templeClip.attach(frame);
  await compilePrograms(renderer, frame.root, { stop });
  if (!stop?.()) uploadTextures(renderer, frame.root);
}
